import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { AlertOctagon, Loader2, Users, RefreshCcw, MessageSquare, MessageCircle } from "lucide-react";

/**
 * CancelOpdDialog — large red "Cancel Today's OPD / Urgent Leave" CTA
 * that mass-cancels every active booking for the day, triggers refunds
 * + bulk SMS + WhatsApp, and optionally flips the doctor to on_leave.
 */
export default function CancelOpdDialog({ api, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [setOnLeave, setSetOnLeave] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => {
    if (busy) return;
    setOpen(false);
    setReason("");
    setConfirmText("");
    setResult(null);
  };

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== "CANCEL OPD") {
      toast.error("Type CANCEL OPD to confirm.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/doctors/me/cancel-opd", {
        reason: reason || "Urgent provider leave",
        set_on_leave: setOnLeave,
      });
      setResult(data);
      toast.success(`${data.affected} booking(s) cancelled. ${data.refunded} refund(s) initiated.`);
      if (onCancelled) onCancelled(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          className="gap-2 h-12 bg-rose-600 hover:bg-rose-700 text-white font-semibold shadow-md shadow-rose-200"
          data-testid="cancel-opd-trigger"
        >
          <AlertOctagon className="w-4 h-4" /> Cancel Today&apos;s OPD / Urgent Leave
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="cancel-opd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertOctagon className="w-5 h-5" /> Cancel Today&apos;s OPD
          </DialogTitle>
          <DialogDescription>
            This will <span className="font-semibold text-rose-700">immediately lock the queue</span>,
            cancel <span className="font-semibold">every active booking</span> for today
            (waiting + checking + pending payment), initiate <span className="font-semibold">100% refunds</span> on all paid
            tickets, and broadcast bulk SMS + WhatsApp notifications. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="cancel-opd-result">
            <ResultRow icon={Users} label="Patients cancelled" value={result.affected} />
            <ResultRow icon={RefreshCcw} label="Refunds initiated" value={result.refunded} />
            {result.razorpay_refunds > 0 && (
              <ResultRow icon={RefreshCcw} label="Razorpay refunds processed" value={result.razorpay_refunds} accent="emerald" />
            )}
            {result.razorpay_refund_failures > 0 && (
              <ResultRow icon={RefreshCcw} label="Razorpay refund failures" value={result.razorpay_refund_failures} accent="rose" />
            )}
            <ResultRow icon={MessageSquare} label="SMS sent" value={result.sms_sent} />
            <ResultRow icon={MessageCircle} label="WhatsApp sent" value={result.whatsapp_sent} />
            {result.next_available_date && (
              <p className="text-xs text-slate-500 mt-2">Patients have been pointed to your next available slot: <span className="font-mono font-semibold text-slate-700">{result.next_available_date}</span></p>
            )}
            <DialogFooter>
              <Button onClick={close} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cancel-opd-close">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold">Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Emergency surgery at District Hospital"
                rows={2}
                data-testid="cancel-opd-reason"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Mark me as On Leave</div>
                <div className="text-xs text-slate-500">Hides you from public search until you toggle Active again.</div>
              </div>
              <Switch checked={setOnLeave} onCheckedChange={setSetOnLeave} data-testid="cancel-opd-on-leave" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-rose-700">Type <span className="font-mono">CANCEL OPD</span> to confirm</Label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full h-10 px-3 mt-1 rounded-md border border-rose-300 bg-rose-50/40 focus:outline-none focus:ring-2 focus:ring-rose-400 font-mono text-sm"
                placeholder="CANCEL OPD"
                data-testid="cancel-opd-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={close} disabled={busy}>Keep OPD open</Button>
              <Button
                onClick={submit}
                disabled={busy || confirmText.trim().toUpperCase() !== "CANCEL OPD"}
                className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
                data-testid="cancel-opd-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? "Cancelling…" : "Cancel OPD now"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultRow({ icon: Icon, label, value, accent }) {
  const cls = accent === "rose"
    ? "text-rose-700 bg-rose-50 border-rose-200"
    : accent === "emerald"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${cls}`}>
      <div className="flex items-center gap-2 text-sm"><Icon className="w-4 h-4" /> {label}</div>
      <div className="font-mono font-bold text-base">{value ?? 0}</div>
    </div>
  );
}
